import { axios, BaseService } from '@/pigeonline/wrapper';
import { app } from '@/main.js';

export default class TextService extends BaseService {
  async matchResponsesToCustomTopics(request) {
    try {
      const URL = app.config.globalProperties.$base_URL + '/analysis/text';

      const response = await axios.post(`${URL}/match_responses_to_custom_topics`, request, {
        headers: this.getAuthHeader(),
      });

      return response.data; // { job_id: "...", status: "queued" }
    } catch (e) {
      throw new Error('textService.js:matchResponsesToCustomTopics ' + e.message);
    }
  }

  async getSuggestedThemes(request) {
    try {
      const URL = app.config.globalProperties.$base_URL + '/analysis/text';

      const response = await axios.post(`${URL}/get_suggested_themes`, request, {
        headers: this.getAuthHeader(),
      });

      return response.data;
    } catch (e) {
      throw new Error('textService.js:getSuggestedThemes ' + e.message);
    }
  }

  async patchSuggestedTheme(request) {
    try {
      const URL = app.config.globalProperties.$base_URL + '/analysis/text';

      const response = await axios.patch(`${URL}/patch_suggested_theme`, request, {
        headers: this.getAuthHeader(),
      });

      return response.data;
    } catch (e) {
      throw new Error('textService.js:patchSuggestedTheme ' + e.message);
    }
  }

  /**
   * Fetch keywords for an OPEN_ENDED client question
   * @param {Object} request
   * @param {String} request.client_question_id
   * @param {String} request.data_set_id data set ID of client questions
   * @returns {Object} key value pair of theme: word list
   */

  async bertSentenceTopicsGenerator(request) {
    try {
      const URL = app.config.globalProperties.$base_URL + '/analysis/text';
      const response = await axios.post(`${URL}/sentence_level_bertopic`, request, {
        headers: this.getAuthHeader(),
      });
      return response.data;
    } catch (e) {
      throw new Error('textService.js:bertSentenceTopicsGenerator ' + e.message);
    }
  }

  async generateLDATopicGenerator(request) {
    try {
      const URL = app.config.globalProperties.$base_URL + '/analysis/text';
      const response = await axios.post(`${URL}/lda_topic_modelling`, request, {
        headers: this.getAuthHeader(),
      });
      return response.data;
    } catch (e) {
      throw new Error('textService.js:generateLDATopicGenerator ' + e.message);
    }
  }

  /**
   * Fetch word associations for a user_string of an OPEN_ENDED client question
   * @param {Object} request
   * @param {String} request.user_string
   * @param {String} request.client_question_id
   * @param {String} request.data_set_id data set ID of client questions
   * @returns {List} of word associations
   */
  async wordAssociations(request) {
    try {
      const URL = app.config.globalProperties.$base_URL + '/analysis/text';
      const response = await axios.post(`${URL}/word_association`, request, {
        headers: this.getAuthHeader(),
      });
      return response.data;
    } catch (e) {
      throw new Error('textService.js:wordAssociations ' + e.message);
    }
  }

  /**
   * NOTE: discontinued as now search just uses applyCoverage
   * Search API: Simple
   * @param {Object} request
   * @param {String} request.search_string
   * @param {String} request.client_question_id
   * @param {String} request.data_set_id data set ID of client questions
   * @returns {List} of words sorted by match strength
   */
  async searchSimple(request) {
    try {
      const URL = app.config.globalProperties.$base_URL + '/analysis/text';
      const response = await axios.post(`${URL}/search_simple`, request, {
        headers: this.getAuthHeader(),
      });
      return response.data;
    } catch (e) {
      throw new Error('textService.js:searchSimple ' + e.message);
    }
  }

  /**
   * Search API: Advanced
   * @param {Object} request
   * @param {String} request.search_string
   * @param {String} request.client_question_id
   * @param {String} request.data_set_id data set ID of client questions
   * @returns {List} of words sorted by match strength
   */
  async searchAdvanced(request) {
    try {
      const URL = app.config.globalProperties.$base_URL + '/analysis/text';
      const response = await axios.post(`${URL}/search_advanced`, request, {
        headers: this.getAuthHeader(),
      });
      return response.data;
    } catch (e) {
      throw new Error('textService.js:searchAdvanced ' + e.message);
    }
  }

  /**
   * Search for similar sentences (BERT-based)
   * @param {Object} request
   * @param {String} request.search_string
   * @param {String} request.client_question_id
   * @returns {List} of words sorted by match strength
   */
  async findSimilarSentences(request) {
    try {
      const URL = app.config.globalProperties.$base_URL + '/analysis/text';
      const response = await axios.post(`${URL}/sentence_similarity`, request, {
        headers: this.getAuthHeader(),
      });
      return response.data;
    } catch (e) {
      throw new Error('textService.js:sentence_similarity ' + e.message);
    }
  }

  /**
   * Fetch sentiments OPEN_ENDED client question
   * @param {Object} request
   * @param {String} request.client_question_id
   * @returns [Object] all text respones with their sentiments
   */
  async lemmas_and_sentiments(request) {
    try {
      const URL = app.config.globalProperties.$base_URL + '/analysis/text';
      const response = await axios.post(`${URL}/lemmas_and_sentiments`, request, {
        headers: this.getAuthHeader(),
      });
      return response.data;
    } catch (e) {
      throw new Error('textService.js:sentiments ' + e.message);
    }
  }

  /**
   * Fetch coverage for a 'search'
   */
  async searchCoverage(request) {
    try {
      const URL = app.config.globalProperties.$base_URL + '/analysis/text';
      const response = await axios.post(`${URL}/themes/search`, request, {
        headers: this.getAuthHeader(),
      });
      return response.data;
    } catch (e) {
      throw new Error('textService.js:searchCoverage ' + e.message);
    }
  }

  async computeSegmentedCoverage(request) {
    try {
      const URL = app.config.globalProperties.$base_URL + '/analysis/text';
      const response = await axios.post(`${URL}/compute_segmented_coverage`, request, {
        headers: this.getAuthHeader(),
      });
      return response.data;
    } catch (e) {
      throw new Error('textService.js:segmentedCoverage ' + e.message);
    }
  }

  async createTheme(request) {
    const URL = app.config.globalProperties.$base_URL + '/analysis/text';
    const response = await axios.post(`${URL}/create_theme`, request, {
      headers: this.getAuthHeader(),
    });
    return response.data;
  }

  async createThemes(request) {
    const URL = app.config.globalProperties.$base_URL + '/analysis/text';
    const response = await axios.post(`${URL}/create_themes`, request, {
      headers: this.getAuthHeader(),
    });
    return response.data;
  }

  async saveThemes(request) {
    try {
      const URL = app.config.globalProperties.$base_URL + '/analysis/text';
      const response = await axios.post(`${URL}/save_themes`, request, {
        headers: this.getAuthHeader(),
      });
      return response.data;
    } catch (e) {
      throw new Error('textService.js:saveThemes ' + e.message);
    }
  }

  async pollThemesForUpdate(request) {
    try {
      const URL = app.config.globalProperties.$base_URL + '/analysis/text';
      const response = await axios.post(`${URL}/poll_themes_for_update`, request, {
        headers: this.getAuthHeader(),
      });
      return response.data;
    } catch (e) {
      throw new Error('textService.js:pollThemesForUpdate ' + e.message);
    }
  }

  async fetchTextThemes(request) {
    try {
      const URL = app.config.globalProperties.$base_URL + '/analysis/text';
      const response = await axios.post(`${URL}/get_themes`, request, {
        headers: this.getAuthHeader(),
      });
      return response.data;
    } catch (e) {
      throw new Error('textService.js:fetchTextThemes ' + e.message);
    }
  }

  async deleteTextTheme(request) {
    try {
      const URL = app.config.globalProperties.$base_URL + '/analysis/text';
      const response = await axios.post(`${URL}/delete_theme`, request, {
        headers: this.getAuthHeader(),
      });
      return response.data;
    } catch (e) {
      throw new Error('textService.js:deleteTextTheme ' + e.message);
    }
  }

  async deleteTextThemes(request) {
    try {
      const URL = app.config.globalProperties.$base_URL + '/analysis/text';
      const response = await axios.post(`${URL}/delete_themes`, request, {
        headers: this.getAuthHeader(),
      });
      return response.data;
    } catch (e) {
      throw new Error('textService.js:deleteTextThemes ' + e.message);
    }
  }

  async updateThemeCoverage(request) {
    const URL = app.config.globalProperties.$base_URL + '/analysis/text';
    const response = await axios.post(`${URL}/update_theme_coverage`, request, {
      headers: this.getAuthHeader(),
    });
    return response.data;
  }

  async deleteSuggestedTheme(themeId) {
    try {
      const URL = app.config.globalProperties.$base_URL + '/analysis/text';
      const response = await axios.delete(`${URL}/delete_suggested_theme/${themeId}`, {
        headers: this.getAuthHeader(),
      });
      return response.data;
    } catch (e) {
      throw new Error('textService.js:deleteSuggestedTheme ' + e.message);
    }
  }

  async deleteAllSuggestedThemes(request) {
    try {
      const URL = app.config.globalProperties.$base_URL + '/analysis/text';
      const response = await axios.post(`${URL}/delete_all_suggested_themes`, request, {
        headers: this.getAuthHeader(),
      });
      return response.data;
    } catch (e) {
      throw new Error('textService.js:deleteAllSuggestedThemes ' + e.message);
    }
  }

  /**
   * Fetch respondent info for a client question response id
   * @param {Object} request
   * @param {String} request.project_id
   * @param {String} request.client_question_id
   * @param {String} request.response_id
   * @returns [String] 200 OK success
   */
  async respondentInfo(request) {
    try {
      const URL = app.config.globalProperties.$base_URL + '/analysis/text';
      const response = await axios.post(`${URL}/respondent_info`, request, {
        headers: this.getAuthHeader(),
      });
      return response.data;
    } catch (e) {
      throw new Error('textService.js:respondentInfo ' + e.message);
    }
  }

  async downloadTaggedDataExcel(
    projectId,
    clientQuestionId,
    textAnalysisUUID,
    filterParams = {},
    exportMode = 'delimited',
  ) {
    try {
      const BASE = app.config.globalProperties.$base_URL + '/analysis/text/export_responses';

      const filter = encodeURIComponent(JSON.stringify(filterParams));
      const url = `${BASE}/${projectId}/${clientQuestionId}/${textAnalysisUUID}/?format=${exportMode}&filterParams=${filter}`;

      const response = await axios.get(url, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const objectUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `text_analysis_${textAnalysisUUID}.xlsx`;
      link.click();

      window.URL.revokeObjectURL(objectUrl);

      return true;
    } catch (e) {
      throw new Error('textService.js:downloadTaggedDataExcel ' + e.message);
    }
  }
}
